import { useQuery } from "@tanstack/react-query"
import type { DocumentUes } from "../../domain/entities/document"
import { makeGetDocumentUes } from "../../domain/use-cases/get-document-ues"
import { httpDocumentRepository } from "../../data/repositories/document-repository"

const getDocumentUes = makeGetDocumentUes(httpDocumentRepository)
const REFRESH_INTERVAL_MS = 10_000
const POLLING_TIMEOUT_MS = 120_000

// Module-level (not useRef/query-state) on purpose: the poll gate must survive
// re-renders AND the re-extract mutation living in a different component tree,
// keyed by docId. Entries are pruned once a terminal status is observed AFTER
// running (sawRunning), on the 120s timeout, and on reextract error
// (stopDocumentUesPolling).
//
// sawRunning guards a race: re-extracting an already-completed/failed
// document returns 202 before the ingestion job flips the ues step to
// "running" in the background, so the immediate post-mutation refetch can
// still read the STALE terminal status from the previous run. Until we've
// actually observed "running", an armed terminal reading is treated as
// stale and polling continues (bounded by the 120s timeout backstop).
const extractionSnapshots = new Map<
  string,
  { startedAt: number; sawRunning: boolean }
>()

export const startDocumentUesPolling = (docId: string) => {
  extractionSnapshots.set(docId, { startedAt: Date.now(), sawRunning: false })
}

export const stopDocumentUesPolling = (docId: string) => {
  extractionSnapshots.delete(docId)
}

export const documentUesRefetchInterval = (
  docId: string,
  data: DocumentUes | undefined,
) => {
  const status = data?.status

  const snap = extractionSnapshots.get(docId)
  if (!snap) return status === "running" ? REFRESH_INTERVAL_MS : false

  if (Date.now() - snap.startedAt >= POLLING_TIMEOUT_MS) {
    extractionSnapshots.delete(docId)
    return false
  }

  if (status === "running") {
    snap.sawRunning = true
    return REFRESH_INTERVAL_MS
  }

  if ((status === "completed" || status === "failed") && snap.sawRunning) {
    extractionSnapshots.delete(docId)
    return false
  }

  // Armed, and either queued or a stale terminal reading from before the
  // background job flipped to "running" — keep polling until running is
  // observed (or the timeout backstop above fires).
  return REFRESH_INTERVAL_MS
}

export const useDocumentUes = (docId: string) =>
  useQuery({
    queryKey: ["documentUes", docId],
    queryFn: () => getDocumentUes(docId),
    enabled: !!docId,
    refetchInterval: (query) =>
      documentUesRefetchInterval(docId, query.state.data),
  })
